import { useMemo } from "react";
import { AgGrid, Button, Flex, Grid, ModalV2, Text } from "@jsq/ds/core";
import {
  TemplateStatusBadge,
  type TemplateStatus,
} from "~/pages/Reporting/components/TemplateStatusBadge";
import { useAgGridDefaults } from "~/pages/UserAndRoles/utils/gridConfig";
import { SPACING } from "~/styles/variables";
import * as styles from "./styles_vanilla.css";
import type { QueryUsageTemplate } from "./types";

type QueryInUseWarningModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onProceed: () => void;
  templates: QueryUsageTemplate[];
};

/** Display order for the default status sort, most actionable first:
 * Active (approved system templates) -> Approved for use -> Waiting for
 * client authorization -> Template denied -> Archived. */
const STATUS_SORT_RANK: Record<TemplateStatus, number> = {
  active: 0,
  approved: 1,
  pending: 2,
  denied: 3,
  archived: 4,
};

/** Status rank first, then template name alphabetically within a status. */
const statusComparator: AgGrid.ColDef<QueryUsageTemplate, TemplateStatus>["comparator"] = (
  a,
  b,
  nodeA,
  nodeB,
) => {
  const rankDiff =
    (a ? STATUS_SORT_RANK[a] : Number.MAX_SAFE_INTEGER) -
    (b ? STATUS_SORT_RANK[b] : Number.MAX_SAFE_INTEGER);
  if (rankDiff !== 0) return rankDiff;
  return (nodeA?.data?.templateName ?? "").localeCompare(nodeB?.data?.templateName ?? "");
};

const StatusCellRenderer = (
  params: AgGrid.ICellRendererParams<QueryUsageTemplate, TemplateStatus>,
) => {
  if (!params.value) return null;
  return <TemplateStatusBadge status={params.value} iconSize="small" />;
};

const UsageEmptyOverlay = () => (
  <Flex
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
    gap={SPACING.X_SMALL}
  >
    <Text variant="strong-md" align="center" className={styles.mutedHeading}>
      No active templates reference this query
    </Text>
  </Flex>
);

export const QueryInUseWarningModal = ({
  isOpen,
  onClose,
  onProceed,
  templates,
}: QueryInUseWarningModalProps) => {
  const columnDefs = useMemo<AgGrid.ColDef<QueryUsageTemplate>[]>(
    () => [
      {
        field: "templateName",
        headerName: "Template name",
        sortable: true,
        flex: 1,
      },
      {
        field: "clientOrSystem",
        headerName: "Client or system",
        sortable: true,
        flex: 1,
      },
      {
        field: "status",
        headerName: "Status",
        sortable: true,
        flex: 1,
        cellRenderer: StatusCellRenderer,
        comparator: statusComparator,
        sort: "asc",
      },
    ],
    [],
  );

  const { gridOptions } = useAgGridDefaults({
    overrides: {
      domLayout: "autoHeight",
      rowHeight: 42,
      headerHeight: 40,
      suppressContextMenu: true,
      suppressCellFocus: true,
      suppressHorizontalScroll: true,
      pagination: false,
      sideBar: false,
      statusBar: undefined,
    },
  });

  return (
    <ModalV2
      show={isOpen}
      onHide={onClose}
      title="Query is in use"
      size="large"
      data-testid="query-in-use-warning-modal"
    >
      <ModalV2.Body>
        <Flex flexDirection="column" gap={SPACING.MEDIUM}>
          <Text variant="secondary-md">
            Review the downstream impacts of any updates to the query before proceeding.
          </Text>
          <div className={styles.usageGrid} data-testid="query-usage-table">
            <Grid<QueryUsageTemplate>
              rowData={templates}
              getRowId={(params) => params.data.id}
              columnDefs={columnDefs}
              gridOptions={gridOptions}
              noRowsOverlayComponent={UsageEmptyOverlay}
            />
          </div>
        </Flex>
      </ModalV2.Body>
      <ModalV2.Footer>
        <Flex justifyContent="flex-end" gap={SPACING.SMALL}>
          <Button variant="neutral" onClick={onClose} data-testid="query-in-use-cancel-btn">
            Cancel
          </Button>
          <Button variant="primary" onClick={onProceed} data-testid="query-in-use-proceed-btn">
            Proceed to update
          </Button>
        </Flex>
      </ModalV2.Footer>
    </ModalV2>
  );
};
